import { ConfirmDialog } from "@/components/Dialogs/ConfirmDialog"
import { CatalogManager } from "@/lib/catalog"
import { routes } from "@/utils/routes"
import { pushDialog, replaceDialog, resetDialogs } from "../dialogs-state"
import { proxy } from "comlink"
import { sessionIdAtom } from "../app-state"
import { store } from "../store"
import { getOpfsPathSegments } from "@/utils/opfs-path"
import { closeSession } from "@/routing/project-session"
// eslint-disable-next-line no-restricted-imports -- Phase 1: store-layer dialog i18n
import i18n from "@/i18n"

CatalogManager.onProjectLocked(
	proxy(() => {
		console.log("[Project Lock] multiple access detected")
		CatalogManager.pauseLock()
		showProjectDuplicateAccessDialog().then((openAnyway) => {
			resetDialogs()
			if (!openAnyway) {
				void closeSession({ reason: "duplicate_project_access" })
			} else {
				// Pass undefined to use this.directory in the worker
				CatalogManager.lockProject(undefined, store.get(sessionIdAtom))
			}
		})
	}),
)

export async function isProjectLocked(handle?: FileSystemDirectoryHandle) {
	const sessionId = store.get(sessionIdAtom)

	// If a handle is provided, we need to check the lock without relying on
	// the catalog being open (e.g., when deleting a project from home screen).
	// We pass both the handle (works in Chrome) and OPFS path segments (Safari fallback)
	// since FileSystemDirectoryHandle cannot be transferred to workers in Safari.
	if (handle) {
		const opfsPathSegments = getOpfsPathSegments(handle)
		const isLocked = await CatalogManager.checkProjectLock(
			opfsPathSegments ? undefined : handle,
			sessionId,
			opfsPathSegments,
		)
		if (isLocked) {
			const openAnyway = await showProjectAlreadyActiveDialog()
			resetDialogs()
			if (!openAnyway) {
				return true
			}
		}
		return false
	}

	// Otherwise, use the catalog worker with this.directory (catalog must be open).
	const isLocked = await CatalogManager.checkProjectLock(undefined, sessionId)
	if (isLocked) {
		const openAnyway = await showProjectAlreadyActiveDialog()
		resetDialogs()
		if (!openAnyway) {
			return true
		}
	}
	return false
}

export function showProjectAlreadyActiveDialog(): Promise<boolean> {
	return new Promise((resolve) => {
		replaceDialog(ConfirmDialog, {
			trackingViewName: "project_already_active_dialog",
			title: i18n.t("store:lockProject.projectAlreadyActiveTitle"),
			message: i18n.t("store:lockProject.projectAlreadyActiveMessage"),
			secondary: {
				title: i18n.t("store:lockProject.openAnyway"),
				handler: () => {
					pushDialog(ConfirmDialog, {
						trackingViewName: "project_access_warning_dialog",
						title: i18n.t("store:lockProject.openAnyway"),
						message: i18n.t("store:lockProject.multiWindowWarningMessage"),
						secondary: {
							title: i18n.t("common:action.open"),
							handler: async () => {
								await CatalogManager.removeProjectLock()
								resolve(true)
							},
						},
						primary: {
							title: i18n.t("common:action.cancel"),
							handler: () => {
								void import("@/router").then(({ router }) => {
									void routes.root.navigate(router)
								})
								resolve(false)
							},
						},
					})
				},
			},
			primary: {
				title: i18n.t("common:action.cancel"),
				handler: () => {
					void import("@/router").then(({ router }) => {
						void routes.root.navigate(router)
					})
					resolve(false)
				},
			},
		})
	})
}

export function showProjectDuplicateAccessDialog(): Promise<boolean> {
	return new Promise((resolve) => {
		replaceDialog(ConfirmDialog, {
			trackingViewName: "project_duplicate_access_dialog",
			title: i18n.t("store:lockProject.projectAlreadyActiveTitle"),
			message: i18n.t("store:lockProject.projectDuplicateAccessMessage"),
			secondary: {
				title: i18n.t("store:lockProject.stayInProject"),
				handler: () => {
					pushDialog(ConfirmDialog, {
						trackingViewName: "project_access_warning_dialog",
						title: i18n.t("store:lockProject.stayInProject"),
						message: i18n.t("store:lockProject.stayInProjectWarningMessage"),
						secondary: {
							title: i18n.t("common:action.stay"),
							handler: async () => {
								await CatalogManager.removeProjectLock()
								resolve(true)
							},
						},
						primary: {
							title: i18n.t("common:action.cancel"),
							handler: () => resolve(false),
						},
					})
				},
			},
			primary: {
				title: i18n.t("store:lockProject.errorCloseProject"),
				handler: () => resolve(false),
			},
		})
	})
}
